import { useState, useEffect } from 'react';
import Layout from '@/components/layout/Layout';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { useEmpresa } from '@/contexts/EmpresaContext';
import { useTranslation } from 'react-i18next';

export default function ConfiguracoesPage() {
  const { isAuthenticated } = useAdminAuth();
  const { empresaSelecionada } = useEmpresa();
  const { t } = useTranslation();

  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [activeSection, setActiveSection] = useState('geral');

  // Geral
  const [idioma, setIdioma] = useState('pt-BR');
  const [moeda, setMoeda] = useState('BRL');
  const [timezone, setTimezone] = useState('America/Sao_Paulo');
  const [formatoData, setFormatoData] = useState('DD/MM/YYYY');
  const [autoLogout, setAutoLogout] = useState('30');

  // Notificações
  const [notifEmail, setNotifEmail] = useState(true);
  const [notifPush, setNotifPush] = useState(true);
  const [notifCampanha, setNotifCampanha] = useState(true);
  const [notifMissao, setNotifMissao] = useState(true);
  const [notifResgate, setNotifResgate] = useState(true);
  const [notifFinanceiro, setNotifFinanceiro] = useState(true);
  const [notifAuditoria, setNotifAuditoria] = useState(false);
  const [notifMarketing, setNotifMarketing] = useState(false);
  const [somNotif, setSomNotif] = useState(true);

  // Segurança
  const [mfa, setMfa] = useState(false);
  const [loginAlert, setLoginAlert] = useState(true);
  const [ipRestrito, setIpRestrito] = useState(false);
  const [ips, setIps] = useState('192.168.1.0/24');
  const [sessaoTimeout, setSessaoTimeout] = useState('60');

  // Aparência
  const [tema, setTema] = useState('light');
  const [densidade, setDensidade] = useState('normal');
  const [tamanhoFonte, setTamanhoFonte] = useState('14');
  const [animacoes, setAnimacoes] = useState(true);

  const mostrarToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleSalvar = () => {
    mostrarToast('Configurações salvas com sucesso', 'success');
  };

  const handleRestaurar = () => {
    setIdioma('pt-BR');
    setMoeda('BRL');
    setTimezone('America/Sao_Paulo');
    setFormatoData('DD/MM/YYYY');
    setAutoLogout('30');
    setNotifEmail(true);
    setNotifPush(true);
    setNotifCampanha(true);
    setNotifMissao(true);
    setNotifResgate(true);
    setNotifFinanceiro(true);
    setNotifAuditoria(false);
    setNotifMarketing(false);
    setSomNotif(true);
    setMfa(false);
    setLoginAlert(true);
    setIpRestrito(false);
    setIps('192.168.1.0/24');
    setSessaoTimeout('60');
    setTema('light');
    setDensidade('normal');
    setTamanhoFonte('14');
    setAnimacoes(true);
    mostrarToast('Configurações restauradas para o padrão', 'success');
  };

  useEffect(() => {
    if (idioma === 'pt-BR') {
      t('');
    }
  }, [idioma, t]);

  if (!isAuthenticated) return null;

  const sections = [
    { id: 'geral', label: 'Geral', icon: 'ri-settings-3-line' },
    { id: 'notificacoes', label: 'Notificações', icon: 'ri-notification-3-line' },
    { id: 'seguranca', label: 'Segurança', icon: 'ri-shield-check-line' },
    { id: 'aparencia', label: 'Aparência', icon: 'ri-palette-line' },
  ];

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Configurações</h1>
            <p className="text-sm text-gray-500 mt-1">
              Personalize a experiência do portal da {empresaSelecionada.nome}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleRestaurar}
              className="px-3 py-1.5 text-xs font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer transition-colors whitespace-nowrap"
            >
              Restaurar padrão
            </button>
            <button
              onClick={handleSalvar}
              className="px-4 py-1.5 bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white font-bold text-xs rounded-lg transition-all cursor-pointer whitespace-nowrap shadow-md"
            >
              Salvar
            </button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar de seções */}
          <div className="lg:w-64 flex-shrink-0">
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 text-left text-sm font-bold transition-all cursor-pointer whitespace-nowrap border-l-3 ${
                    activeSection === section.id
                      ? 'bg-orange-50 text-orange-600 border-l-orange-500'
                      : 'text-gray-600 hover:bg-gray-50 border-l-transparent'
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={section.icon}></i>
                  </div>
                  <span>{section.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Conteúdo */}
          <div className="flex-1 min-w-0">
            {/* ─── GERAL ─── */}
            {activeSection === 'geral' && (
              <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-6">
                <h3 className="text-sm font-bold text-gray-900 flex items-center space-x-2 mb-4">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-settings-3-line text-orange-500"></i>
                  </div>
                  <span>Configurações Gerais</span>
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5">Idioma</label>
                    <select value={idioma} onChange={(e) => setIdioma(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 transition-all cursor-pointer">
                      <option value="pt-BR">Português (Brasil)</option>
                      <option value="en-US">English (US)</option>
                      <option value="es-ES">Español</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5">Moeda padrão</label>
                    <select value={moeda} onChange={(e) => setMoeda(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 transition-all cursor-pointer">
                      <option value="BRL">Real (R$)</option>
                      <option value="USD">Dólar (US$)</option>
                      <option value="EUR">Euro (€)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5">Fuso horário</label>
                    <select value={timezone} onChange={(e) => setTimezone(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 transition-all cursor-pointer">
                      <option value="America/Sao_Paulo">São Paulo (GMT-3)</option>
                      <option value="America/New_York">New York (GMT-5)</option>
                      <option value="Europe/London">London (GMT+0)</option>
                      <option value="Asia/Tokyo">Tokyo (GMT+9)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5">Formato de data</label>
                    <select value={formatoData} onChange={(e) => setFormatoData(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 transition-all cursor-pointer">
                      <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                      <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                      <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5">Auto logout (minutos)</label>
                    <select value={autoLogout} onChange={(e) => setAutoLogout(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 transition-all cursor-pointer">
                      <option value="15">15 minutos</option>
                      <option value="30">30 minutos</option>
                      <option value="60">1 hora</option>
                      <option value="120">2 horas</option>
                      <option value="0">Nunca</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* ─── NOTIFICAÇÕES ─── */}
            {activeSection === 'notificacoes' && (
              <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-6">
                <h3 className="text-sm font-bold text-gray-900 flex items-center space-x-2 mb-4">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-notification-3-line text-orange-500"></i>
                  </div>
                  <span>Notificações</span>
                </h3>

                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 rounded-lg border border-gray-100 hover:bg-gray-50/50 transition-colors">
                    <div className="flex items-center space-x-3">
                      <div className="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-mail-line text-blue-600 text-sm"></i>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-800">Email</p>
                        <p className="text-xs text-gray-500">Receber notificações por email</p>
                      </div>
                    </div>
                    <button onClick={() => setNotifEmail(!notifEmail)}
                      className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${notifEmail ? 'bg-emerald-500' : 'bg-gray-200'}`}>
                      <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow-sm transition-all ${notifEmail ? 'left-6' : 'left-1'}`}></span>
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-lg border border-gray-100 hover:bg-gray-50/50 transition-colors">
                    <div className="flex items-center space-x-3">
                      <div className="w-9 h-9 bg-violet-50 rounded-lg flex items-center justify-center">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-smartphone-line text-violet-600 text-sm"></i>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-800">Push no navegador</p>
                        <p className="text-xs text-gray-500">Notificações push no desktop</p>
                      </div>
                    </div>
                    <button onClick={() => setNotifPush(!notifPush)}
                      className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${notifPush ? 'bg-emerald-500' : 'bg-gray-200'}`}>
                      <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow-sm transition-all ${notifPush ? 'left-6' : 'left-1'}`}></span>
                    </button>
                  </div>

                  <div className="border-t border-gray-100 pt-4">
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Tipos de notificação</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {[
                        { label: 'Campanhas', desc: 'Início, fim e atualizações', state: notifCampanha, set: setNotifCampanha, icon: 'ri-megaphone-line', color: 'bg-orange-50 text-orange-600' },
                        { label: 'Missões', desc: 'Conclusões e novas missões', state: notifMissao, set: setNotifMissao, icon: 'ri-focus-line', color: 'bg-emerald-50 text-emerald-600' },
                        { label: 'Resgates', desc: 'Confirmações de resgate', state: notifResgate, set: setNotifResgate, icon: 'ri-gift-line', color: 'bg-purple-50 text-purple-600' },
                        { label: 'Financeiro', desc: 'Transações e saldo', state: notifFinanceiro, set: setNotifFinanceiro, icon: 'ri-coin-line', color: 'bg-amber-50 text-amber-600' },
                        { label: 'Auditoria', desc: 'Alertas e relatórios', state: notifAuditoria, set: setNotifAuditoria, icon: 'ri-shield-check-line', color: 'bg-sky-50 text-sky-600' },
                        { label: 'Marketing', desc: 'Novidades e comunicados', state: notifMarketing, set: setNotifMarketing, icon: 'ri-mail-send-line', color: 'bg-rose-50 text-rose-600' },
                      ].map((item) => (
                        <div key={item.label} className="flex items-center justify-between p-2.5 rounded-lg border border-gray-100 hover:bg-gray-50/50 transition-colors">
                          <div className="flex items-center space-x-2.5">
                            <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${item.color}`}>
                              <div className="w-3.5 h-3.5 flex items-center justify-center">
                                <i className={`${item.icon} text-xs`}></i>
                              </div>
                            </div>
                            <div>
                              <p className="text-xs font-bold text-gray-800">{item.label}</p>
                              <p className="text-[10px] text-gray-500">{item.desc}</p>
                            </div>
                          </div>
                          <button onClick={() => item.set(!item.state)}
                            className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer ${item.state ? 'bg-emerald-500' : 'bg-gray-200'}`}>
                            <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-all ${item.state ? 'left-5' : 'left-0.5'}`}></span>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-lg border border-gray-100 hover:bg-gray-50/50 transition-colors">
                    <div className="flex items-center space-x-3">
                      <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-volume-up-line text-gray-600 text-sm"></i>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-800">Som de notificação</p>
                        <p className="text-xs text-gray-500">Tocar alerta sonoro</p>
                      </div>
                    </div>
                    <button onClick={() => setSomNotif(!somNotif)}
                      className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${somNotif ? 'bg-emerald-500' : 'bg-gray-200'}`}>
                      <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow-sm transition-all ${somNotif ? 'left-6' : 'left-1'}`}></span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ─── SEGURANÇA ─── */}
            {activeSection === 'seguranca' && (
              <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-6">
                <h3 className="text-sm font-bold text-gray-900 flex items-center space-x-2 mb-4">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-shield-check-line text-orange-500"></i>
                  </div>
                  <span>Segurança</span>
                </h3>

                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 rounded-lg border border-gray-100 hover:bg-gray-50/50 transition-colors">
                    <div className="flex items-center space-x-3">
                      <div className="w-9 h-9 bg-amber-50 rounded-lg flex items-center justify-center">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-lock-line text-amber-600 text-sm"></i>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-800">Autenticação de dois fatores</p>
                        <p className="text-xs text-gray-500">Exigir MFA para login</p>
                      </div>
                    </div>
                    <button onClick={() => setMfa(!mfa)}
                      className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${mfa ? 'bg-emerald-500' : 'bg-gray-200'}`}>
                      <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow-sm transition-all ${mfa ? 'left-6' : 'left-1'}`}></span>
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-lg border border-gray-100 hover:bg-gray-50/50 transition-colors">
                    <div className="flex items-center space-x-3">
                      <div className="w-9 h-9 bg-rose-50 rounded-lg flex items-center justify-center">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-alarm-warning-line text-rose-600 text-sm"></i>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-800">Alerta de novo login</p>
                        <p className="text-xs text-gray-500">Notificar quando houver login de novo dispositivo</p>
                      </div>
                    </div>
                    <button onClick={() => setLoginAlert(!loginAlert)}
                      className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${loginAlert ? 'bg-emerald-500' : 'bg-gray-200'}`}>
                      <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow-sm transition-all ${loginAlert ? 'left-6' : 'left-1'}`}></span>
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-lg border border-gray-100 hover:bg-gray-50/50 transition-colors">
                    <div className="flex items-center space-x-3">
                      <div className="w-9 h-9 bg-sky-50 rounded-lg flex items-center justify-center">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-wifi-line text-sky-600 text-sm"></i>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-800">Restrição de IP</p>
                        <p className="text-xs text-gray-500">Limitar acesso a IPs autorizados</p>
                      </div>
                    </div>
                    <button onClick={() => setIpRestrito(!ipRestrito)}
                      className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${ipRestrito ? 'bg-emerald-500' : 'bg-gray-200'}`}>
                      <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow-sm transition-all ${ipRestrito ? 'left-6' : 'left-1'}`}></span>
                    </button>
                  </div>

                  {ipRestrito && (
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1.5">IPs autorizados</label>
                      <textarea
                        value={ips}
                        onChange={(e) => setIps(e.target.value)}
                        placeholder="192.168.1.0/24, 10.0.0.0/8"
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 transition-all resize-none"
                        rows={3}
                        maxLength={500}
                      />
                      <p className="text-[10px] text-gray-400 mt-1">Separe múltiplos IPs por vírgula</p>
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5">Timeout de sessão (minutos)</label>
                    <select value={sessaoTimeout} onChange={(e) => setSessaoTimeout(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 transition-all cursor-pointer">
                      <option value="15">15 minutos</option>
                      <option value="30">30 minutos</option>
                      <option value="60">1 hora</option>
                      <option value="120">2 horas</option>
                      <option value="240">4 horas</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* ─── APARÊNCIA ─── */}
            {activeSection === 'aparencia' && (
              <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-6">
                <h3 className="text-sm font-bold text-gray-900 flex items-center space-x-2 mb-4">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-palette-line text-orange-500"></i>
                  </div>
                  <span>Aparência</span>
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-2">Tema</label>
                    <div className="flex space-x-3">
                      {[
                        { value: 'light', label: 'Claro', icon: 'ri-sun-line', bg: 'bg-white', border: 'border-gray-200', preview: 'bg-gray-100' },
                        { value: 'dark', label: 'Escuro', icon: 'ri-moon-line', bg: 'bg-gray-800', border: 'border-gray-700', preview: 'bg-gray-700' },
                        { value: 'auto', label: 'Automático', icon: 'ri-contrast-line', bg: 'bg-gradient-to-br from-gray-100 to-gray-800', border: 'border-gray-300', preview: 'bg-gradient-to-r from-gray-100 to-gray-800' },
                      ].map((t) => (
                        <button key={t.value} onClick={() => setTema(t.value)}
                          className={`flex-1 p-3 rounded-xl border-2 transition-all cursor-pointer ${tema === t.value ? 'border-orange-500 bg-orange-50' : 'border-gray-100 hover:border-gray-200'}`}>
                          <div className={`w-8 h-8 rounded-lg ${t.bg} border ${t.border} flex items-center justify-center mx-auto mb-2`}>
                            <div className="w-4 h-4 flex items-center justify-center">
                              <i className={`${t.icon} text-sm ${t.value === 'dark' ? 'text-white' : 'text-gray-600'}`}></i>
                            </div>
                          </div>
                          <p className="text-xs font-bold text-gray-700 text-center">{t.label}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="border-t border-gray-100 pt-4">
                    <label className="block text-xs font-bold text-gray-700 mb-2">Densidade da interface</label>
                    <div className="flex space-x-3">
                      {[
                        { value: 'compact', label: 'Compacta', desc: 'Menos espaçamento' },
                        { value: 'normal', label: 'Normal', desc: 'Padrão' },
                        { value: 'comfortable', label: 'Confortável', desc: 'Mais espaçamento' },
                      ].map((d) => (
                        <button key={d.value} onClick={() => setDensidade(d.value)}
                          className={`flex-1 p-3 rounded-xl border-2 transition-all cursor-pointer text-left ${densidade === d.value ? 'border-orange-500 bg-orange-50' : 'border-gray-100 hover:border-gray-200'}`}>
                          <p className="text-xs font-bold text-gray-700">{d.label}</p>
                          <p className="text-[10px] text-gray-500 mt-0.5">{d.desc}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="border-t border-gray-100 pt-4">
                    <label className="block text-xs font-bold text-gray-700 mb-2">Tamanho da fonte</label>
                    <div className="flex items-center space-x-3">
                      <span className="text-xs text-gray-400">A</span>
                      <input type="range" min="12" max="18" value={tamanhoFonte} onChange={(e) => setTamanhoFonte(e.target.value)}
                        className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-orange-500" />
                      <span className="text-base text-gray-700 font-bold">A</span>
                    </div>
                    <p className="text-[10px] text-gray-400 mt-1">{tamanhoFonte}px</p>
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-lg border border-gray-100 hover:bg-gray-50/50 transition-colors">
                    <div className="flex items-center space-x-3">
                      <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-sparkling-line text-gray-600 text-sm"></i>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-800">Animações</p>
                        <p className="text-xs text-gray-500">Transições e efeitos visuais</p>
                      </div>
                    </div>
                    <button onClick={() => setAnimacoes(!animacoes)}
                      className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${animacoes ? 'bg-emerald-500' : 'bg-gray-200'}`}>
                      <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow-sm transition-all ${animacoes ? 'left-6' : 'left-1'}`}></span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Toast */}
        {toast && (
          <div className={`fixed bottom-6 right-6 px-4 py-3 rounded-xl shadow-lg text-sm font-bold flex items-center space-x-2 z-50 transition-all ${toast.type === 'success' ? 'bg-emerald-600 text-white' : 'bg-red-500 text-white'}`}>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={toast.type === 'success' ? 'ri-check-line text-sm' : 'ri-error-warning-line text-sm'}></i>
            </div>
            <span>{toast.message}</span>
          </div>
        )}
      </div>
    </Layout>
  );
}